import { Flex, Input } from "@mantine/core";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import styles from "./stepStore.module.scss";

interface CodeInputProps {
	setCode: (code: string) => void;
	onEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

const CodeInput: FC<CodeInputProps> = ({ setCode, onEnter }) => {
	const inputsRef = useRef<HTMLInputElement[]>([]);
	const [values, setValues] = useState<string[]>(new Array(6).fill(""));

	const [inputErrors, setInputErrors] = useState<boolean[]>(new Array(6).fill(false));

	const focusNextInput = (index: number) => {
		if (index < 5) {
			inputsRef.current[index + 1].focus();
		}
	};

	const updateValues = (newValues: string[]) => {
		setValues(newValues);
		setCode(newValues.join(""));
	};

	const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		const newValues = [...values];
		const newInputErrors = [...inputErrors];

		// Check if the last character in the value is a digit
		if (value === "" || /^[0-9]$/.test(value[value.length - 1])) {
			newValues[index] = value;
			newInputErrors[index] = false;
			updateValues(newValues);
			if (value.length > 0) {
				focusNextInput(index);
			}
		} else {
			// If the last character is not a digit, reset the value to the previous state
			event.target.value = values[index];
			newInputErrors[index] = true; // Set error for current input
		}
		setInputErrors(newInputErrors);
	};

	const handleKeydown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
		const newInputErrors = [...inputErrors];
		const { key, currentTarget } = event;
		const { selectionStart, selectionEnd } = currentTarget;

		// Check if the cursor is at the start of the input
		const atStart = selectionStart === 0 && selectionEnd === 0;
		// Check if the cursor is at the end of the input
		const atEnd = selectionStart === currentTarget.value.length;

		if (key === "Enter" && onEnter) {
			if (inputErrors.every((value) => value === false) && values.every((value) => value !== "")) {
				onEnter(event);
				return;
			}
		}

		if (key === "ArrowLeft" && atStart && index > 0) {
			// Move focus to the previous input field
			event.preventDefault(); // Prevent the default action to keep the cursor position
			inputsRef.current[index - 1].focus();
			return;
		}
		if (key === "ArrowRight" && atEnd && index < inputsRef.current.length - 1) {
			event.preventDefault();
			inputsRef.current[index + 1].focus();
			return;
		}

		if (event.key === "Backspace") {
			if (values[index] === "" && index > 0) {
				// If current is empty, clear the previous one and focus it
				const newValues = [...values];
				newValues[index - 1] = "";
				updateValues(newValues);
				inputsRef.current[index - 1].focus();
			} else {
				// If current is not empty, just clear the current one
				const newValues = [...values];
				newValues[index] = "";
				updateValues(newValues);
			}
		}
		newInputErrors[index] = false; // Reset error for current input
		setInputErrors(newInputErrors);
	};

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		const pasteContent = event.clipboardData.getData("text");
		// Match the first occurrence of 6 consecutive digits
		const match = pasteContent.match(/\d{6}/);

		if (match) {
			const paste = match[0]; // The first match of 6 consecutive digits
			const newValues = paste.split("");
			updateValues(newValues);
			setInputErrors(new Array(6).fill(false));

			// Focus on the next input if there's space, otherwise focus on the last input
			const nextIndex = newValues.length < 6 ? newValues.length : 5;
			inputsRef.current[nextIndex].focus();
			inputsRef.current[nextIndex].select();
		} else {
			// If no 6 consecutive digits found, set errors for all inputs
			setInputErrors(new Array(6).fill(true));
		}
	};

	const inputStyle: React.CSSProperties = {
		width: 50,
		marginRight: 10,
		fontWeight: "bold"
	};

	useEffect(() => {
		inputsRef.current[0].focus();
	}, []);

	return (
		<Flex justify={"center"} w={"100%"}>
			{values.map((value, index) => (
				<Input
					key={index}
					value={value}
					onChange={(event) => handleChange(index, event)}
					onKeyDown={(event) => handleKeydown(index, event)}
					onPaste={handlePaste}
					maxLength={1}
					style={inputStyle}
					ref={(el: HTMLInputElement) => (inputsRef.current[index] = el)}
					required
					error={inputErrors[index]}
					classNames={{
						input: styles.codeInputStyle
					}}
				/>
			))}
		</Flex>
	);
};

export default CodeInput;
